import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/de';
import './Home.css';
import './ConsultationView.css';
import './modals/ConsultationInvitation.css';
import { useNavigate, useLocation } from "react-router-dom";
import ScheduleConsultationModal from './modals/ScheduleConsultationModal';
import ConsultationReplyDialog from './consultations/ConsultationReplyDialog';
// prime-ink-mail-replies-v1
import DirectBookingFields from './consultations/DirectBookingFields';
import './consultations/direct-booking.css';
// prime-ink-direct-booking-v1
import ConvertToTattooModal from './consultations/ConvertToTattooModal';


import {
  FaWhatsapp
} from "react-icons/fa";

import {
  FiEdit2,
  FiPhone,
  FiMail,
  FiClock,
  FiAlertTriangle,
  FiImage,
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
  FiCalendar,
  FiXCircle,
  FiTrash2,
  FiPlus,
  FiRefreshCw,
  FiMoreVertical,
  FiUsers,
  FiMessageCircle,
  FiCheckCircle,
  FiHelpCircle,
  FiUserX,
  FiChevronDown,
  FiChevronUp,
  FiUserPlus
} from 'react-icons/fi';

moment.locale('de');

const needsNewConsultation = item =>
  item.status === 'waiting' && (item.invitation_state === 'reschedule_requested' || item.price_reply_requested === true);

// Read the latest customer response, preserving multiline availability text.
export const customerResponsePreview = note => {
  const entries = String(note || '').split(/^Kundenrückmeldung \(\d{2}\.\d{2}\.\d{2}(?:\d{2})?,?[^\n]*\): /m);
  if (entries.length < 2) return '';
  const latest = entries[entries.length - 1].trim();
  const choice = latest.split('\n')[0].trim();
  const marker = '\nWunsch / Nachricht: ';
  const index = latest.indexOf(marker);
  return index < 0 ? choice : `${choice}\n${latest.slice(index + marker.length).trim()}`;
};

const ConsultationView = ({ locations, selectedLocationId, setSelectedLocationId }) => {
  const routeLocation = useLocation();
  const pipelineRequest = useRef(null);
  const [invitationToast, setInvitationToast] = useState(null);
  const [pendingSends, setPendingSends] = useState([]);
  const [priceReplies, setPriceReplies] = useState({});
  const priceReplyStates = useRef({});
  priceReplyStates.current = priceReplies;
  const priceRequests = useRef(new Map());
  const priceSubmitting = useRef(new Set());

  const sendPriceReply = async item => {
    if (priceSubmitting.current.has(item.id)) return;
    priceSubmitting.current.add(item.id);
    setPriceReplies(previous => ({...previous, [item.id]: {state: 'submitting'}}));
    if (!priceRequests.current.has(item.id)) priceRequests.current.set(item.id, crypto.randomUUID());
    try {
      const res = await axios.post(`/api/consultations/${item.id}/price-reply`, {requestKey: priceRequests.current.get(item.id)});
      setPriceReplies(previous => ({...previous, [item.id]: res.data}));
      if (res.data.state === 'sent') {window.dispatchEvent(new Event('consultations-changed')); void fetchConsultations();}
      setInvitationToast({kind: res.data.state === 'sent' ? 'success' : 'pending', message: res.data.state === 'sent'
        ? 'Preisauskunft erfolgreich versendet.' : 'Preisauskunft gespeichert · wird gesendet …'});
    } catch (err) {
      setPriceReplies(previous => ({...previous, [item.id]: {state: 'check'}}));
      setInvitationToast({kind: 'error', message: err.response?.data?.message || 'Versand noch nicht bestätigt. Bitte den Status prüfen.'});
    } finally {priceSubmitting.current.delete(item.id);}
  };

  const priceButton = item => {
    if (!item.email || !['new', 'contacted', 'waiting'].includes(item.status)) return null;
    const state = priceReplies[item.id]?.state;
    const blocked = !state || ['submitting', 'queued', 'sending', 'sent', 'uncertain', 'failed', 'check'].includes(state);
    const label = {sent: 'Preisauskunft versendet', queued: 'Preisauskunft wird gesendet …', sending: 'Preisauskunft wird gesendet …',
      submitting: 'Preisauskunft wird gespeichert …', uncertain: 'Versand prüfen', failed: 'Versand fehlgeschlagen', check: 'Versandstatus wird geprüft …'}[state] || 'Preisauskunft senden';
    return <button type="button" className="pipeline-price-reply-button" disabled={blocked}
      title="Standardantwort zu Preisen per E-Mail senden" onClick={() => sendPriceReply(item)}><FiMail />{label}</button>;
  };


  const notifyInvitation = useCallback(result => {
    if (!result?.id) return;
    if (['sent', 'confirmed'].includes(result.state)) {
      setInvitationToast({kind: 'success', message: 'Einladung erfolgreich versendet.'});
    } else if (['queued', 'sending'].includes(result.state)) {
      setInvitationToast({kind: 'pending', message: 'Einladung gespeichert · wird gesendet …'});
      setPendingSends(previous => previous.some(item => item.id === result.id)
        ? previous : [...previous, {id: result.id, startedAt: Date.now()}]);
    } else if (['failed', 'uncertain', 'cancelled', 'expired'].includes(result.state)) {
      setInvitationToast({kind: 'error', message: result.state === 'uncertain'
        ? 'Versandstatus unklar. Bitte das Postfach prüfen.'
        : 'Einladung wurde nicht versendet. Bitte den Einladungsstatus prüfen.'});
    }
  }, []);

  useEffect(() => {
    if (!invitationToast) return;
    const timer = setTimeout(() => setInvitationToast(null), invitationToast.kind === 'error' ? 8000 : 3000);
    return () => clearTimeout(timer);
  }, [invitationToast]);

  // The scheduling dialog closes once accepted; keep watching the actual SMTP result here.
  useEffect(() => {
    if (!pendingSends.length) return;
    const controller = new AbortController();
    let timer;
    const poll = async () => {
      const completed = [];
      await Promise.all(pendingSends.map(async item => {
        try {
          const res = await axios.get(`/api/consultation-invitations/${item.id}/status`, {signal: controller.signal});
          if (controller.signal.aborted) return;
          if (!['queued', 'sending'].includes(res.data.state)) {
            completed.push(item.id);
            notifyInvitation({...res.data, id: item.id});
            window.dispatchEvent(new Event('consultations-changed'));
            return;
          }
        } catch {
          // A status lookup failure must never trigger another email send.
        }
        if (!controller.signal.aborted && Date.now() - item.startedAt >= 120000) {
          completed.push(item.id);
          setInvitationToast({kind: 'pending', message: 'Versand noch nicht bestätigt. Bitte den Einladungsstatus prüfen.'});
        }
      }));
      if (controller.signal.aborted) return;
      if (completed.length) {
        setPendingSends(previous => previous.filter(item => !completed.includes(item.id)));
      } else {
        timer = setTimeout(poll, 2000);
      }
    };
    void poll();
    return () => {controller.abort(); clearTimeout(timer);};
  }, [pendingSends, notifyInvitation]);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );
  const [mobileMoreFiltersOpen, setMobileMoreFiltersOpen] = useState(false);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [appointmentToConvert, setAppointmentToConvert] = useState(null);
  const [pendingConsultationId, setPendingConsultationId] = useState(null);
  const [openDayMenuId, setOpenDayMenuId] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailAppointment, setDetailAppointment] = useState(null);
  const [consultationDayModalOpen, setConsultationDayModalOpen] = useState(false);
  const [newConsultationDay, setNewConsultationDay] = useState('');
  const [consultations, setConsultations] = useState([]);
  const [consultationDays, setConsultationDays] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [replyItem, setReplyItem] = useState(null);
  const [filter, setFilter] = useState('new');
  const [responseSummary, setResponseSummary] = useState({locationId: null, items: []});
  const newTermRequests = String(responseSummary.locationId) === String(selectedLocationId)
    ? responseSummary.items : [];
  const newTermRequestCount = newTermRequests.length;

  // Watch actionable customer replies even when the visible filter is "Neu".
  useEffect(() => {
    if (!selectedLocationId) return;
    let stopped = false;
    let controller;
    const refresh = async () => {
      controller?.abort();
      const request = new AbortController();
      controller = request;
      try {
        const res = await axios.get('/api/consultations', {
          params: {locationId: selectedLocationId, status: 'waiting'},
          signal: request.signal,
        });
        if (!stopped && !request.signal.aborted) {
          setResponseSummary({locationId: selectedLocationId,
            items: (Array.isArray(res.data) ? res.data : []).filter(needsNewConsultation)});
        }
      } catch (err) {
        if (!stopped && !request.signal.aborted) {
          console.error('Kundenrückmeldungen konnten nicht geladen werden:', err.response?.data || err);
        }
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    void refresh();
    const timer = setInterval(refreshWhenVisible, 15000);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('focus', refreshWhenVisible);
    return () => {
      stopped = true; controller?.abort(); clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('focus', refreshWhenVisible);
    };
  }, [selectedLocationId, filter]);
  const [loading, setLoading] = useState(false);
  
   const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  
  
  const [, setSelectedConsultationAppointment] = useState(null);
  

  const deleteConsultationDay = async (day) => {
    const count = day.appointments?.length || 0;

    const message =
      count > 0
        ? `Diesen Beratungstag wirklich löschen? Es sind noch ${count} Termine eingetragen.`
        : 'Diesen Beratungstag wirklich löschen?';

    if (!window.confirm(message)) return;

    try {
      await axios.delete(`/api/consultation_days/${day.dayId}`);
      setOpenDayMenuId(null);
      await fetchConsultationDays();
    } catch (err) {
      console.error('Beratungstag konnte nicht gelöscht werden:', err.response?.data || err);
    }
  };
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(routeLocation.search);
    if (params.get('tasks') !== '1') return;
    const targetId = params.get('locationId');
    if (['1', '2', '3'].includes(targetId) && setSelectedLocationId) {
      setSelectedLocationId(targetId);
    }
    setFilter(params.get('kind') === 'new' ? 'new' : params.get('kind') === 'change' ? 'changes' : 'tasks'); setSelectedId(null); setPendingConsultationId(null);
    navigate(routeLocation.pathname, {replace: true});
  }, [routeLocation.search, routeLocation.pathname, navigate, setSelectedLocationId]);
  useEffect(() => () => pipelineRequest.current?.abort(), [selectedLocationId]);
  const selectedLocationName =
    locations.find(loc => Number(loc.id) === Number(selectedLocationId))?.name || 'Unbekannt';

  const [editingConsultationId, setEditingConsultationId] = useState(null);
  const emptyManualForm = {
    clientName: '',
    phone: '',
    email: '',
    subject: '',
    description: '',
    note: '',
    source: 'phone',
  };

  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [manualForm, setManualForm] = useState(emptyManualForm);
  const [manualSaving,setManualSaving]=useState(false);
  const [manualError,setManualError]=useState('');
  const manualRequestKey=useRef(crypto.randomUUID());
  useEffect(()=>{manualRequestKey.current=crypto.randomUUID();setManualError('');},[manualForm,manualModalOpen,selectedLocationId]);

  const getStatus = (item) => item?.status || 'new';

  const getImages = (item) => {
    if (!item?.image_url) return [];
    return item.image_url.split(',').map(url => url.trim()).filter(Boolean);
  };

  const getAgeLabel = (date) => {
    if (!date) return 'unbekannt';

    const created = moment(date);
    const now = moment();

    const hours = now.diff(created, 'hours');
    const days = now.diff(created, 'days');

    if (hours < 1) return 'gerade eben';
    if (hours < 24) return `seit ${hours} Std.`;
    if (days === 1) return 'seit 1 Tag';

    return `seit ${days} Tagen`;
  };

  const isOverdue = (item) =>
    getStatus(item) === 'waiting' &&
    moment().diff(moment(item.updated_at || item.created_at), 'days') >= 3;

  const fetchConsultations = useCallback(async () => {
    if (!selectedLocationId) return;
    pipelineRequest.current?.abort();
    const controller = new AbortController();
    pipelineRequest.current = controller;
    const get = status => axios.get('/api/consultations', {
      params: {locationId: selectedLocationId, status}, signal: controller.signal,
    });
    try {
      setLoading(true);
      let rows;
      if (filter === 'tasks') {
        const [fresh, waiting] = await Promise.all([get('new'), get('waiting')]);
        const changes = (Array.isArray(waiting.data) ? waiting.data : []).filter(needsNewConsultation);
        rows = [...changes, ...(Array.isArray(fresh.data) ? fresh.data : [])];
        rows = [...new Map(rows.map(item => [item.id, item])).values()];
      } else if (filter === 'changes') {
        const res = await get('waiting');
        rows = (Array.isArray(res.data) ? res.data : []).filter(needsNewConsultation);
      } else {
        const res = await get(filter);
        rows = Array.isArray(res.data) ? res.data : [];
      }
      if (!controller.signal.aborted) setConsultations(rows);
    } catch (err) {
      if (!controller.signal.aborted) console.error('Fehler beim Laden der Pipeline:', err.response?.data || err);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [selectedLocationId, filter]);

  const fetchConsultationDays = useCallback(async () => {
    if (!selectedLocationId) return;

    try {
      const res = await axios.get(
        `/api/consultation_days_with_appointments?locationId=${selectedLocationId}`
      );
      setConsultationDays(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Fehler beim Laden der Beratungstage:', err.response?.data || err);
    }
  }, [selectedLocationId]);

  

  const updateConsultationStatus = async (id, status) => {
    try {
      await axios.put(`/api/consultations/${id}/status`, { status });

      setConsultations(prev =>
        prev.map(item => (item.id === id ? { ...item, status } : item))
      );

      await fetchConsultations();
    } catch (err) {
      console.error('Status konnte nicht geändert werden:', err.response?.data || err);
    }
  };

  const markAsNotInquiry = async (item) => {
    if (!item?.id) return;

    const confirmed = window.confirm(
      `${item.clientName || 'Diese Nachricht'} als „Keine Anfrage“ markieren?\n\n` +
      'Sie verschwindet aus der Pipeline und wird nicht mehr in Insights gezählt.'
    );

    if (!confirmed) return;

    try {
      await axios.put(`/api/consultations/${item.id}/status`, {
        status: 'not_inquiry',
      });

      setConsultations(previous =>
        previous.filter(entry => Number(entry.id) !== Number(item.id))
      );
      setSelectedId(null);
      window.dispatchEvent(new Event('consultations-changed'));
      await fetchConsultations();
    } catch (err) {
      console.error(
        'Anfrage konnte nicht ausgeblendet werden:',
        err.response?.data || err
      );
    }
  };

  const setActiveConsultationStatus = async (status) => {
    if (!activeItem?.id) return;
    await updateConsultationStatus(activeItem.id, status);
  };

  const setActiveConsultationWaiting = async () => {
    if (!activeItem?.id) return;
    await updateConsultationStatus(activeItem.id, 'waiting');
  };

  



  useEffect(() => {
    let cancelled = false;
    const timer = setInterval(() => {
      if (!cancelled && document.visibilityState === 'visible') {
        void fetchConsultationDays();
        void fetchConsultations();
      }
    }, 15000);
    return () => {cancelled = true; clearInterval(timer);};
  }, [selectedLocationId, filter, fetchConsultationDays, fetchConsultations]);

  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleModalItem, setScheduleModalItem] = useState(null);
  const [scheduleAppointmentId, setScheduleAppointmentId] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    dayId: '',
    time: '11:00',
  });

  const scheduleAppointment = useMemo(() => {
    if (!scheduleAppointmentId) return null;

    for (const day of consultationDays) {
      const appointment = (day.appointments || []).find(
        item => Number(item.id) === Number(scheduleAppointmentId)
      );

      if (appointment) {
        return appointment;
      }
    }

    return null;
  }, [consultationDays, scheduleAppointmentId]);

  const convertScheduledConsultation = () => {
    if (!scheduleAppointmentId) {
      console.error("Keine Beratungstermin-ID zum Umwandeln vorhanden");
      return;
    }

    let appointmentToUse = null;

    consultationDays.forEach(day => {
      const foundAppointment = (day.appointments || []).find(
        appointment =>
          Number(appointment.id) === Number(scheduleAppointmentId)
      );

      if (foundAppointment) {
        appointmentToUse = {
          ...foundAppointment,
          dayId: foundAppointment.dayId || day.dayId,
          day: foundAppointment.day || day.day,
        };
      }
    });

    if (!appointmentToUse) {
      console.error(
        "Beratungstermin wurde nicht gefunden:",
        scheduleAppointmentId
      );
      return;
    }

    setAppointmentToConvert(appointmentToUse);
    setScheduleModalOpen(false);
    setScheduleModalItem(null);
    setScheduleAppointmentId(null);
    setConvertModalOpen(true);
  };

  const findExistingConsultationAppointment = (consultationId) => {
    for (const day of consultationDays) {
      const appointment = day.appointments?.find(
        appt => Number(appt.consultation_id) === Number(consultationId)
      );

      if (appointment) {
        return { appointment, day };
      }
    }

    return null;
  };

  const openScheduleModal = () => {
    if (!activeItem) return;

    setScheduleModalItem(activeItem);

    const existing = findExistingConsultationAppointment(activeItem.id);

    if (existing) {
      setScheduleAppointmentId(existing.appointment.id);

      setScheduleForm({
        dayId: String(existing.day.dayId),
        time: moment(
          existing.appointment.time,
          ["HH:mm", "HH:mm:ss"]
        ).format("HH:mm"),
      });
    } else {
      setScheduleAppointmentId(null);

      setScheduleForm({
        dayId: upcomingConsultationDays[0]
          ? String(upcomingConsultationDays[0].dayId)
          : "",
        time: "11:00",
      });
    }

    setScheduleModalOpen(true);
  };

  const openAppointmentScheduleModal = async (appointment, day) => {
    if (!appointment || !day) return;

    let modalItem = {
      ...appointment,
      id: appointment.consultation_id || appointment.id,
      clientName: appointment.clientName || 'Beratungstermin',
    };

    if (appointment.consultation_id) {
      try {
        const res = await axios.get(
          `/api/consultations/${appointment.consultation_id}`
        );

        modalItem = {
          ...res.data,
          clientName:
            res.data?.clientName ||
            appointment.clientName ||
            'Beratungstermin',
        };
      } catch (err) {
        console.error(
          'Beratungsanfrage für Terminbearbeitung konnte nicht geladen werden:',
          err.response?.data || err
        );
      }
    }

    setScheduleModalItem(modalItem);
    setScheduleAppointmentId(appointment.id);
    setScheduleForm({
      dayId: String(day.dayId),
      time: moment(
        appointment.time,
        ['HH:mm', 'HH:mm:ss']
      ).format('HH:mm'),
    });

    if (appointment.consultation_id) {
      setSelectedId(appointment.consultation_id);
    }

    setScheduleModalOpen(true);
  };

  const saveConsultationAppointment = async (options = {}) => {
    if (options.refreshOnly) {
      await Promise.all([fetchConsultationDays(), fetchConsultations()]);
      return;
    }
    if (options.directBooking) {
      const item=scheduleModalItem||activeItem;
      if(!item?.id)throw new Error('Keine Kundenanfrage ausgewählt.');
      const response=await axios.post(`/api/consultations/${item.id}/direct-booking`,{dayId:Number(scheduleForm.dayId),time:scheduleForm.time,requestKey:options.requestKey,sendConfirmation:options.sendConfirmation});
      setScheduleModalOpen(false);setScheduleModalItem(null);setScheduleAppointmentId(null);
      setInvitationToast({kind:'success',message:response.data.sendConfirmation?'Termin fest gebucht · Bestätigungsmail wird gesendet.':'Termin fest gebucht.'});
      await Promise.all([fetchConsultationDays(),fetchConsultations()]);
      return response.data;
    }
    if (options.sendInvitation) {
      const item = scheduleModalItem || activeItem;
      if (!item?.id) throw new Error('Keine Kundenanfrage ausgewählt.');
      const response = await axios.post(`/api/consultations/${item.id}/invitations`, {
        dayId: Number(scheduleForm.dayId), time: scheduleForm.time,
        subject: options.subject, body: options.body, expiresHours: options.expiresHours,
        requestKey: options.requestKey, originalMail: options.originalMail,
      });
      await Promise.all([fetchConsultationDays(), fetchConsultations()]);
      notifyInvitation(response.data);
      return response.data;
    }
    if (!scheduleForm.dayId || !scheduleForm.time) return;

    try {
      if (scheduleAppointmentId) {
        const existingAppointment = consultationDays
          .flatMap(day => day.appointments || [])
          .find(
            appointment =>
              Number(appointment.id) === Number(scheduleAppointmentId)
          );

        if (!existingAppointment) {
          console.error(
            "Bestehender Beratungstermin wurde nicht gefunden:",
            scheduleAppointmentId
          );
          return;
        }

        await axios.put(`/api/appointments/${scheduleAppointmentId}`, {
          dayId: Number(scheduleForm.dayId),
          time: scheduleForm.time,
          clientName: existingAppointment.clientName || "",
          phone: existingAppointment.phone || "",
          email: existingAppointment.email || "",
          description: existingAppointment.description || "",
          note: existingAppointment.note || "",
          minRange: existingAppointment.minRange || 0,
          maxRange: existingAppointment.maxRange || 0,
          deposit: existingAppointment.deposit || 0,
          duration: existingAppointment.duration || 30,
          tattoo_category: existingAppointment.tattoo_category || "",
        });
      } else {
        if (!activeItem) return;

        await axios.post("/api/appointments", {
          consultation_id: activeItem.id,
          dayId: Number(scheduleForm.dayId),
          dayType: "consultation",
          time: scheduleForm.time,
          clientName: activeItem.clientName || "",
          phone: activeItem.phone || "",
          email: activeItem.email || "",
          description: `${activeItem.subject || ""}\n\n${activeItem.description || ""}`,
          minRange: 0,
          maxRange: 0,
          deposit: 0,
          duration: 15,
        });

        await axios.put(`/api/consultations/${activeItem.id}/status`, {
          status: "scheduled",
        });
      }

      setScheduleModalOpen(false);
      setScheduleModalItem(null);
      setScheduleAppointmentId(null);

      await fetchConsultationDays();
      await fetchConsultations();
    } catch (err) {
      console.error(
        "Beratungstermin konnte nicht gespeichert werden:",
        err.response?.data || err
      );
      throw err;
    }
  };
  

  const saveManualConsultation = async () => {
    if(manualSaving)return;
    if(!manualForm.clientName.trim()){setManualError('Bitte den Kundennamen eingeben.');return;}
    if(!editingConsultationId&&manualForm.bookNow&&(!manualForm.bookingDayId||!manualForm.bookingTime)){setManualError('Bitte Beratungstag und Uhrzeit wählen.');return;}
    setManualSaving(true);setManualError('');
    try {
      if(editingConsultationId){await axios.put(`/api/consultations/${editingConsultationId}`,manualForm);}
      else if(manualForm.bookNow){
        await axios.post('/api/consultations/direct-booking',{...manualForm,locationId:selectedLocationId,dayId:Number(manualForm.bookingDayId),time:manualForm.bookingTime,sendConfirmation:Boolean(manualForm.sendConfirmation),requestKey:manualRequestKey.current});
        setInvitationToast({kind:'success',message:manualForm.sendConfirmation?'Termin fest gebucht · Bestätigungsmail wird gesendet.':'Termin fest gebucht.'});
      }else{await axios.post('/api/consultations/manual',{locationId:selectedLocationId,...manualForm});}
      setEditingConsultationId(null);setManualForm(emptyManualForm);setManualModalOpen(false);
      await Promise.all([fetchConsultations(),fetchConsultationDays()]);
    }catch(err){setManualError(err.response?.data?.message||err.response?.data?.error||'Speichern fehlgeschlagen. Bitte erneut versuchen.');}
    finally{setManualSaving(false);}
  };

  const deleteActiveConsultation = async () => {
    if (!activeItem) return;

    const ok = window.confirm(
      `${activeItem.clientName || 'Diese Beratung'} wirklich löschen?`
    );

    if (!ok) return;

    try {
      await axios.delete(`/api/consultations/incoming/${activeItem.id}`);

      setSelectedId(null);
      await fetchConsultations();
    } catch (err) {
      console.error(
        'Beratung konnte nicht gelöscht werden:',
        err.response?.data || err
      );
    }
  };

  const saveConsultationDay = async () => {
    if (!newConsultationDay || !selectedLocationId) return;

    const formattedDay = moment(
      newConsultationDay,
      ['YYYY-MM-DD', 'DD.MM.YYYY'],
      true
    ).format('YYYY-MM-DD');

    try {
      console.log('Beratungstag speichern:', {
        day: formattedDay,
        locationId: selectedLocationId,
      });

      await axios.post('/api/consultation_days', {
        day: formattedDay,
        locationId: selectedLocationId,
      });

      setNewConsultationDay('');
      setConsultationDayModalOpen(false);

      await fetchConsultationDays();
    } catch (err) {
      console.error(
        'Beratungstag konnte nicht angelegt werden:',
        err.response?.data || err
      );
    }
  };

  
  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(media.matches);

    update();
    media.addEventListener?.('change', update);

    return () => media.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    fetchConsultations();
    fetchConsultationDays();
  }, [selectedLocationId, filter, fetchConsultations, fetchConsultationDays]);



  const filteredConsultations = useMemo(() => {
    if (filter !== 'waiting') return consultations;
    return [...consultations].sort((a, b) =>
      Number(needsNewConsultation(b)) - Number(needsNewConsultation(a))
    );
  }, [consultations, filter]);

  useEffect(() => {
    if (!pendingConsultationId) return;
    if (!filteredConsultations.length) return;

    const exists = filteredConsultations.find(
      c => Number(c.id) === Number(pendingConsultationId)
    );

    if (exists) {
      setSelectedId(exists.id);
      setPendingConsultationId(null);
    }
  }, [filteredConsultations, pendingConsultationId]);

  const activeItem =
    filteredConsultations.find(item => item.id === selectedId) ||
    (!isMobile ? filteredConsultations[0] : null) ||
    null;

  useEffect(() => {
    if (!activeItem?.id || !activeItem.email) return;
    const id = activeItem.id;
    const controller = new AbortController();
    let timer;
    const poll = async () => {
      try {
        const res = await axios.get(`/api/consultations/${id}/price-reply`, {signal: controller.signal});
        if (controller.signal.aborted) return;
        const old = priceReplyStates.current[id]?.state;
        if (res.data.state === 'sent' && ['queued', 'sending', 'submitting', 'check'].includes(old)) {
          setInvitationToast({kind: 'success', message: 'Preisauskunft erfolgreich versendet · Anfrage wartet.'});
          window.dispatchEvent(new Event('consultations-changed'));
          void fetchConsultations();
        } else if (['failed', 'uncertain'].includes(res.data.state) && old !== res.data.state) {
          setInvitationToast({kind: 'error', message: res.data.state === 'failed' ? 'Preisauskunft konnte nicht versendet werden.' : 'Versandstatus unklar. Bitte das Postfach prüfen.'});
        }
        setPriceReplies(previous => ({...previous, [id]: res.data}));
      } catch {
        // Keep the button disabled while the previous send cannot be ruled out.
      }
      if (!controller.signal.aborted) timer = setTimeout(poll, 2000);
    };
    void poll();
    return () => {controller.abort(); clearTimeout(timer);};
  }, [activeItem?.id, activeItem?.email, fetchConsultations]);

  const activeImages = getImages(activeItem);
  const detailImages = getImages(detailAppointment); useEffect(() => {
    if (pendingConsultationId) return;

    if (filteredConsultations.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }

    const exists = filteredConsultations.some(
      item => Number(item.id) === Number(selectedId)
    );

    if (!exists && !isMobile) {
      setSelectedId(filteredConsultations[0].id);
    }
  }, [filteredConsultations, selectedId, pendingConsultationId, isMobile]);

  useEffect(() => {
    setActiveImageIndex(0);
    setImageViewerOpen(false);
  }, [activeItem?.id]);

  const changeImage = (direction) => {
    if (!activeImages.length) return;

    setActiveImageIndex(prev => {
      if (direction === 'next') {
        return prev === activeImages.length - 1 ? 0 : prev + 1;
      }

      return prev === 0 ? activeImages.length - 1 : prev - 1;
    });
  };

  const upcomingConsultationDays = useMemo(() => {
    return consultationDays
      .filter(day => moment(day.day).isSameOrAfter(moment(), 'day'))
      .sort((a, b) => new Date(a.day) - new Date(b.day));
  }, [consultationDays]);

  

  const statusLabel = {
    new: 'Neu',
    contacted: 'Kontaktiert',
    waiting: 'Wartet',
    scheduled: 'Beratung geplant',
    consulted: 'Beraten',
    tattoo_scheduled: 'Tattoo geplant',
    completed: 'Abgeschlossen',
    converted: 'Umgewandelt',
    cancelled: 'Abgesagt',
    no_show: 'No Show',
  };




  const filterButtons = [
    { key: 'tasks', label: 'Aufgaben' },
    { key: 'changes', label: 'Terminwünsche' },
    { key: 'new', label: 'Neu' },
    { key: 'waiting', label: 'Wartet' },
    { key: 'scheduled', label: 'Beratung' },
    { key: 'consulted', label: 'Historie' },
    { key: 'tattoo_scheduled', label: 'Tattoo geplant' },
    { key: 'cancelled', label: 'Abgesagt' },
    { key: 'all', label: 'Alle' },
  ];
  const openConsultationFromAppointment = async (appt, day) => {
    let consultation = null;

    if (appt.consultation_id) {
      try {
        const res = await axios.get(
          `/api/consultations/${appt.consultation_id}`
        );

        consultation = res.data;
      } catch (err) {
        console.error(
          'Beratungsanfrage konnte nicht geladen werden:',
          err.response?.data || err
        );
      }
    }

    setDetailAppointment({
      ...appt,

      dayId: day.dayId,
      day: day.day,

      consultation_id:
        appt.consultation_id ||
        consultation?.id ||
        null,

      clientName:
        appt.clientName ||
        consultation?.clientName ||
        'Ohne Name',

      phone:
        appt.phone ||
        consultation?.phone ||
        '',

      email:
        appt.email ||
        consultation?.email ||
        '',

      subject:
        consultation?.subject ||
        appt.subject ||
        '',

      description:
        consultation?.description ||
        appt.description ||
        '',

      note:
        appt.note ||
        consultation?.note ||
        '',

      image_url:
        consultation?.image_url ||
        appt.image_url ||
        '',
    });

    setDetailModalOpen(true);
  };
  const deleteConsultationAppointment = async (appointmentId) => {
    if (!window.confirm('Beratungstermin wirklich löschen?')) return;

    try {
      await axios.delete(`/api/appointments/${appointmentId}`);
      setSelectedConsultationAppointment(null);
      await fetchConsultationDays();
    } catch (err) {
      console.error('Beratungstermin konnte nicht gelöscht werden:', err.response?.data || err);
    }
  };

  const consultationAppointmentCount = upcomingConsultationDays.reduce(
    (count, day) => count + (day.appointments?.length || 0),
    0
  );

  return (
    <div className="home-container pipeline-page">
      {invitationToast && <div className={`pipeline-invitation-toast toast-${invitationToast.kind}`}
        role={invitationToast.kind === 'error' ? 'alert' : 'status'} aria-atomic="true">
        {invitationToast.kind === 'success' ? <FiCheckCircle aria-hidden="true" /> : <FiMail aria-hidden="true" />}
        <span>{invitationToast.message}</span>
        <button type="button" aria-label="Meldung schließen" onClick={() => setInvitationToast(null)}>×</button>
      </div>}
      <section className="pipeline-shell">
        <div className="pipeline-header">
          <div>
            <h1>Pipeline</h1>
            <p>{selectedLocationName} · Beratungsanfragen</p>
          </div>

          <div className="pipeline-header-actions">
            <button
              className="pipeline-btn refresh-btn"
              onClick={() => {
                fetchConsultations();
                fetchConsultationDays();
              }}
            >
              <FiRefreshCw />
              Aktualisieren
            </button>

          </div>
        </div>



        <div className="pipeline-status-tabs">
          {filterButtons
            .filter((button) =>
              !isMobile ||
              ['tasks', 'changes', 'new', 'waiting'].includes(button.key) ||
              mobileMoreFiltersOpen
            )
            .map((button) => (
              <button
                key={button.key}
                className={`${filter === button.key ? 'active' : ''} ${button.key === 'waiting' && newTermRequestCount > 0 ? 'pipeline-response-attention' : ''}`}
                aria-label={button.key === 'waiting' && newTermRequestCount > 0
                  ? `Wartet – ${newTermRequestCount} neue Terminwünsche` : button.label}
                onClick={() => setFilter(button.key)}
              >
                {button.label}
                {button.key === 'waiting' && newTermRequestCount > 0 && (
                  <span className="pipeline-response-count" aria-hidden="true">{newTermRequestCount}</span>
                )}
              </button>
            ))}

          {isMobile && (
            <button
              type="button"
              className="pipeline-more-filters"
              onClick={() => setMobileMoreFiltersOpen(prev => !prev)}
              aria-expanded={mobileMoreFiltersOpen}
            >
              {mobileMoreFiltersOpen ? <FiChevronUp /> : <FiChevronDown />}
              {mobileMoreFiltersOpen ? 'Weniger' : 'Mehr'}
            </button>
          )}
        </div>

        {newTermRequestCount > 0 && (
          <div className="pipeline-response-notice" role="status">
            <FiMessageCircle aria-hidden="true" />
            <div>
              <strong>{newTermRequestCount === 1
                ? 'Ein Kunde wünscht einen neuen Termin'
                : `${newTermRequestCount} Kunden wünschen einen neuen Termin`}</strong>
              <span>Bitte die Rückmeldung prüfen und einen passenden Termin anbieten.</span>
            </div>
            <button type="button" onClick={() => {
              setFilter('waiting');
              setPendingConsultationId(newTermRequests[0].id);
              setSelectedId(newTermRequests[0].id);
            }}>Ansehen <FiChevronRight aria-hidden="true" /></button>
          </div>
        )}

        <div className={`pipeline-crm-layout ${isMobile && activeItem ? 'mobile-has-selection' : ''}`}>


          <div className="pipeline-list">
            {loading && <p className="pipeline-empty">Lade Anfragen...</p>}

            {!loading && filteredConsultations.length === 0 && (
              <p className="pipeline-empty">Keine Anfragen</p>
            )}

            {filteredConsultations.map(item => {
              const customerReply = needsNewConsultation(item) ? customerResponsePreview(item.note) : '';
              const status = getStatus(item);
              const images = getImages(item);
              const isExpanded = isMobile && Number(selectedId) === Number(item.id);

              return (
                <div
                  key={item.id}
                  className={`pipeline-list-card ${activeItem?.id === item.id ? 'active' : ''} ${isOverdue(item) ? 'overdue' : ''} ${isExpanded ? 'mobile-expanded' : ''} ${needsNewConsultation(item) ? 'pipeline-response-card' : ''}`} 
                >
                  <button
                    type="button"
                    className="pipeline-list-card-trigger"
                    onClick={() => {
                      if (isMobile) {
                        setSelectedId(prev => Number(prev) === Number(item.id) ? null : item.id);
                      } else {
                        setSelectedId(item.id);
                      }
                    }}
                    aria-expanded={isExpanded}
                  >
                    <div className="pipeline-list-card-top">
                      <strong>{item.clientName || 'Ohne Name'}</strong>
                      <span className={`pipeline-status-dot status-${status}`} />
                    </div>

                    {item.price_reply_requested && <small className="consultation-offer-label">Beratungstermin gewünscht</small>}
                    {item.invitation_state && <small className="consultation-offer-label">
                        {{reschedule_requested:'Neuer Termin gewünscht',declined:'Vom Kunden abgesagt',expired:'Angebot abgelaufen',cancelled:'Angebot storniert',failed:'Versand fehlgeschlagen',sent:'Wartet auf Bestätigung',queued:'Einladung wird gesendet',sending:'Versand läuft',uncertain:'Versand prüfen',confirmed:'Termin bestätigt'}[item.invitation_state]}
                      </small>}
                      {customerReply && <span className="pipeline-customer-reply">
                        <span className="pipeline-customer-reply-title">Kundenwunsch</span>
                        <span>{customerReply}</span>
                      </span>}
                      <span className="pipeline-list-subject">
                      {item.subject || 'Keine Betreffzeile'}
                    </span>

                    <div className="pipeline-list-meta">
                      <span>
                        {isOverdue(item) ? <FiAlertTriangle /> : <FiClock />}
                        {getAgeLabel(item.updated_at || item.created_at)}
                      </span>

                      <span className="pipeline-card-chevron">
                        {images.length > 0 && (
                          <span>
                            <FiImage />
                            {images.length}
                          </span>
                        )}
                        {isMobile && (isExpanded ? <FiChevronUp /> : <FiChevronDown />)}
                      </span>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="pipeline-mobile-inline-detail">
                      <div className="pipeline-mobile-quick-actions">
                        {item.phone && (
                          <a href={`https://wa.me/${item.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer">
                            <FaWhatsapp />
                            WhatsApp
                          </a>
                        )}
                        {item.phone && (
                          <a href={`tel:${item.phone}`}>
                            <FiPhone />
                            Anrufen
                          </a>
                        )}
                        {item.email && (
                          <button type="button" className="pipeline-icon-action purple" title="In der App antworten" aria-label="Auf Kundenanfrage antworten" onClick={() => setReplyItem(item)}> 
                            <FiMail />
                            E-Mail
                          </button>
                        )}
                      </div>

                      <button
                        type="button"
                        className="pipeline-mobile-plan-button"
                        onClick={openScheduleModal}
                      >
                        <FiCalendar />
                        Beratung planen
                      </button>

                      {priceButton(item)}

                      {getStatus(item) === "new" && (
                        <button
                          type="button"
                          className="pipeline-mobile-waiting-button"
                          onClick={(event) => {
                            event.stopPropagation();
                            updateConsultationStatus(item.id, "waiting");
                          }}
                        >
                          <FiMessageCircle />
                          Wartet
                        </button>
                      )}

                      <button
                        type="button"
                        className="pipeline-mobile-not-inquiry-button"
                        onClick={(event) => {
                          event.stopPropagation();
                          markAsNotInquiry(item);
                        }}
                      >
                        <FiXCircle />
                        Keine Anfrage
                      </button>

                      {item.description && (
                        <section className="pipeline-mobile-detail-section">
                          <h4>Beschreibung</h4>
                          <p>{item.description}</p>
                        </section>
                      )}

                      {images.length > 0 && (
                        <section className="pipeline-mobile-detail-section">
                          <h4>Bilder</h4>
                          <div className="pipeline-mobile-image-strip">
                            {images.map((url, imageIndex) => (
                              <button
                                key={`${url}-${imageIndex}`}
                                type="button"
                                onClick={() => {
                                  setActiveImageIndex(imageIndex);
                                  setImageViewerOpen(true);
                                }}
                              >
                                <img src={url} alt="" />
                              </button>
                            ))}
                          </div>
                        </section>
                      )}

                      {(item.phone || item.email) && (
                        <section className="pipeline-mobile-detail-section pipeline-mobile-contact">
                          <h4>Kontakt</h4>
                          {item.phone && <a href={`tel:${item.phone}`}><FiPhone /> {item.phone}</a>}
                          {item.email && <span style={{display:"block", overflowWrap:"anywhere", color:"#b996ed"}}><FiMail /> {item.email}</span>}
                        </section>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <main className="pipeline-detail">
            <div className="pipeline-main-content">              <section className="pipeline-left-detail">
              {activeItem ? (
                <>
                  <div className="pipeline-detail-head premium-row">
                    <span className={`pipeline-status-badge status-top-right status-${getStatus(activeItem)}`}>
                      {statusLabel[getStatus(activeItem)] || getStatus(activeItem)}
                    </span>

                    <div className="pipeline-header-left">
                      <div className="pipeline-client-title">
                        <h2>{activeItem.clientName || "Ohne Name"}</h2>
                        <p>{activeItem.subject || "Keine Betreffzeile"}</p>
                      </div>

                      <div className="pipeline-header-toolbar">
                        {activeItem.phone && (
                          <a href={`tel:${activeItem.phone}`} className="pipeline-icon-action purple">
                            <FiPhone />
                          </a>
                        )}

                        {activeItem.email && (
                          <button type="button" className="pipeline-icon-action purple" title="In der App antworten" aria-label="Auf Kundenanfrage antworten" onClick={() => setReplyItem(activeItem)}> 
                            <FiMail />
                          </button>
                        )}

                        {activeItem.phone && (
                          <a
                            href={`https://wa.me/${activeItem.phone.replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noreferrer"
                            className="pipeline-icon-action purple"
                          >
                            <FaWhatsapp />
                          </a>
                        )}

                        <button type="button" className="pipeline-icon-action purple" aria-label="Beratung planen" onClick={openScheduleModal}>
                          <FiCalendar />
                        </button>

                        <button
                          type="button"
                          className="pipeline-icon-action purple"
                          onClick={() => {
                            setEditingConsultationId(activeItem.id);
                            setManualForm({
                              clientName: activeItem.clientName || '',
                              phone: activeItem.phone || '',
                              email: activeItem.email || '',
                              subject: activeItem.subject || '',
                              description: activeItem.description || '',
                              note: activeItem.note || '',
                              source: activeItem.source || 'manual',
                            });
                            setManualModalOpen(true);
                          }}
                        >
                          <FiEdit2 />
                        </button>
                      </div>

                      {priceButton(activeItem)}

                      <div className="pipeline-status-actions">

                        {getStatus(activeItem) === "new" && (
                          <button
                            className="pipeline-action-btn waiting"
                            onClick={setActiveConsultationWaiting}
                          >
                            <FiMessageCircle />
                            Wartet
                          </button>
                        )}

                        {getStatus(activeItem) === "scheduled" && (
                          <>
                            <button
                              className="pipeline-action-btn success"
                              onClick={() => setActiveConsultationStatus("consulted")}
                            >
                              <FiCheckCircle />
                              Beratung durchgeführt
                            </button>

                            <button
                              className="pipeline-action-btn success"
                              onClick={() => setActiveConsultationStatus("tattoo_scheduled")}
                            >
                              <FiEdit2 />
                              Tattoo geplant
                            </button>

                            <button
                              className="pipeline-action-btn danger"
                              onClick={() => setActiveConsultationStatus("no_show")}
                            >
                              <FiUserX />
                              No Show
                            </button>
                          </>
                        )}

                        {getStatus(activeItem) === "consulted" && (
                          <>
                            <button
                              className="pipeline-action-btn warning"
                              onClick={() => setActiveConsultationStatus("waiting")}
                            >
                              <FiHelpCircle />
                              Überlegt noch
                            </button>

                            <button
                              className="pipeline-action-btn success"
                              onClick={() => setActiveConsultationStatus("tattoo_scheduled")}
                            >
                              <FiEdit2 />                              Tattoo geplant
                            </button>
                          </>
                        )}

                        {getStatus(activeItem) === "waiting" && (
                          <button
                            className="pipeline-action-btn waiting"
                            onClick={setActiveConsultationWaiting}
                          >
                            <FiMessageCircle />
                            Wartet
                          </button>
                        )}

                      </div>
                    </div>
                  </div>

                  <div className="pipeline-media-contact-row">
                    <section className="pipeline-image-section">
                      <div className="pipeline-section-title">
                        <h4>Bilder</h4>
                        {activeImages.length > 1 && (
                          <span>{activeImageIndex + 1} / {activeImages.length}</span>
                        )}
                      </div>

                      {activeImages.length > 0 ? (
                        <>
                          <div className="pipeline-big-image-wrap">
                            <img
                              src={activeImages[activeImageIndex] || activeImages[0]}
                              alt=""
                              className="pipeline-big-image"
                              onClick={() => setImageViewerOpen(true)}
                            />

                            <button type="button" className="pipeline-image-expand" onClick={() => setImageViewerOpen(true)}>
                              <FiMaximize2 />
                            </button>

                            {activeImages.length > 1 && (
                              <>
                                <button type="button" className="pipeline-image-arrow left" onClick={() => changeImage('prev')}>
                                  <FiChevronLeft />
                                </button>

                                <button type="button" className="pipeline-image-arrow right" onClick={() => changeImage('next')}>
                                  <FiChevronRight />
                                </button>
                              </>
                            )}
                          </div>

                          {activeImages.length > 1 && (
                            <div className="pipeline-image-thumbs">
                              {activeImages.map((url, index) => (
                                <button
                                  key={`${url}-${index}`}
                                  type="button"
                                  className={activeImageIndex === index ? 'active' : ''}
                                  onClick={() => setActiveImageIndex(index)}
                                >
                                  <img src={url} alt="" />
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="pipeline-empty">Keine Bilder</p>
                      )}
                    </section>

                    <section className="pipeline-description-section">
                      <h4>Beschreibung</h4>
                      <div className="pipeline-description">
                        {activeItem.description || 'Keine Beschreibung vorhanden.'}
                      </div>
                    </section>
                  </div>

                  {activeItem.note && <section className="pipeline-description-section customer-response-notes" style={{marginBottom: '20px'}}>
                    <h4>Notizen und Kundenrückmeldungen</h4>
                    <div className="pipeline-description" style={{whiteSpace: 'pre-wrap'}}>{activeItem.note}</div>
                  </section>}

                  <section className="pipeline-contact-section">
                    <h4>Kontakt</h4>

                    <div className="pipeline-contact-row">
                      {activeItem.phone && (
                        <a href={`tel:${activeItem.phone}`}>
                          <FiPhone />
                          {activeItem.phone}
                        </a>
                      )}

                      {activeItem.email && (
                        <span style={{display:"block", overflowWrap:"anywhere", color:"#b996ed"}}><FiMail /> {activeItem.email}</span>
                      )}

                      {!activeItem.phone && !activeItem.email && (
                        <span className="pipeline-muted">Keine Kontaktdaten</span>
                      )}
                    </div>
                  </section>

                  <div className="pipeline-danger-zone">
                    <button
                      type="button"
                      className="pipeline-action-btn not-inquiry"
                      onClick={() => markAsNotInquiry(activeItem)}
                    >
                      <FiXCircle />
                      Keine Anfrage
                    </button>

                    <button
                      className="pipeline-action-btn muted-danger"
                      onClick={() => updateConsultationStatus(activeItem.id, 'cancelled')}
                    >
                      Absagen
                    </button>

                    <button className="pipeline-action-btn danger" onClick={deleteActiveConsultation}>
                      <FiTrash2 />
                      Löschen
                    </button>
                  </div>
                </>
              ) : (
                <div className="pipeline-empty-detail">
                  <h2>Keine Anfrage ausgewählt</h2>
                  <p>Links eine Anfrage auswählen.</p>
                </div>
              )}
            </section>


            </div>

          </main>
          <aside className="pipeline-right-consultation">
        <div className="pipeline-section-title">
  <h4
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      margin: 0,
    }}
  >
    Beratungstage

    <span className="pipeline-consultation-count">
      <FiUsers />
      {consultationAppointmentCount}
    </span>
  </h4>

  <div style={{ display: "flex", gap: 8 }}>
    <button
      className="pipeline-add-day-btn"
      onClick={() => {
        setEditingConsultationId(null);
        setManualForm(emptyManualForm);
        setManualModalOpen(true);
      }}
      title="Neue Anfrage"
    >
      <FiUserPlus />
    </button>

    <button
      className="pipeline-add-day-btn"
      onClick={() => setConsultationDayModalOpen(true)}
      title="Beratungstag hinzufügen"
    >
      <FiPlus />
    </button>
  </div>
</div>

            <div className="pipeline-right-days-list">
              {upcomingConsultationDays.length > 0 ? (
                upcomingConsultationDays.map((day) => {
                  const appointments = [...(day.appointments || [])].sort((a, b) =>
                    moment(a.time, ["HH:mm", "HH:mm:ss"]).diff(
                      moment(b.time, ["HH:mm", "HH:mm:ss"])
                    )
                  );

                  return (
                    <div key={day.dayId} className="pipeline-right-day-card">
                      <div className="pipeline-day-header">
                        <div className="pipeline-day-menu-wrap">
                          <button
                            type="button"
                            className="pipeline-day-menu"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDayMenuId(prev => prev === day.dayId ? null : day.dayId);
                            }}
                          >
                            <FiMoreVertical />
                          </button>

                          {openDayMenuId === day.dayId && (
                            <div className="pipeline-day-dropdown">
                              <button
                                type="button"
                                className="danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteConsultationDay(day);
                                }}
                              >
                                <FiTrash2 />
                                Beratungstag löschen
                              </button>
                            </div>
                          )}
                        </div>

                        <strong className="pipeline-day-date">
                          {moment(day.day).format("dd, DD.MM.")}
                        </strong>
                      </div>

                      <div className="pipeline-day-appointments">
                        {appointments.length > 0 ? (
                          appointments.map((appt) => (
                            <button
                              key={appt.id}
                              className={`pipeline-day-appointment ${appt.invitation_state && appt.invitation_state !== 'confirmed' ? 'consultation-awaiting' : ''}`}
                              type="button"
                              onClick={() => openConsultationFromAppointment(appt, day)}
                            >
                              <span className="appointment-time">
                                {moment(appt.time, ["HH:mm", "HH:mm:ss"]).format("HH:mm")}
                              </span>

                              <strong className="appointment-name">
                                {appt.clientName || "Ohne Name"}{appt.invitation_state && appt.invitation_state !== "confirmed" ? " · wartet" : ""}
                              </strong>

                              <span
                                className="appointment-edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAppointmentScheduleModal(appt, day);
                                }}
                              >
                                <FiEdit2 />
                              </span>                      </button>
                          ))
                        ) : (
                          <div className="pipeline-day-empty">
                            Keine Beratungen
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="pipeline-empty">
                  Keine kommenden Beratungstage
                </p>
              )}
            </div>
          </aside>
        </div>

        {imageViewerOpen &&
          (detailModalOpen ? detailImages : activeImages).length > 0 && (
            <div
              className="pipeline-lightbox"
              onClick={() => setImageViewerOpen(false)}
            >
              <button
                type="button"
                className="pipeline-lightbox-close"
                onClick={() => setImageViewerOpen(false)}
              >
                ×
              </button>

              <img
                src={
                  (detailModalOpen
                    ? detailImages
                    : activeImages)[activeImageIndex] ||
                  (detailModalOpen
                    ? detailImages
                    : activeImages)[0]
                }
                alt=""
                onClick={(event) => event.stopPropagation()}
              />
            </div>
          )}

        {detailModalOpen && detailAppointment && (
          <div
            className="pipeline-detail-modal-overlay"
            onClick={() => {
              setDetailModalOpen(false);
              setDetailAppointment(null);
            }}
          >
            <div
              className="pipeline-detail-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="pipeline-detail-modal-close"
                onClick={() => {
                  setDetailModalOpen(false);
                  setDetailAppointment(null);
                }}
                aria-label="Schließen"
              >
                <FiXCircle />
              </button>

              <div className="pipeline-detail-modal-header">
                <div>
                  <span className="pipeline-detail-modal-eyebrow">
                    Beratungstermin
                  </span>

                  <h3>
                    {detailAppointment.clientName || 'Ohne Name'}
                  </h3>

                  {detailAppointment.subject && (
                    <p>{detailAppointment.subject}</p>
                  )}
                </div>

                <div className="pipeline-detail-modal-date">
                  <FiCalendar />

                  <span>
                    {detailAppointment.day
                      ? moment(detailAppointment.day).format(
                        'dd, DD.MM.YYYY'
                      )
                      : 'Kein Datum'}
                  </span>

                  <strong>
                    {detailAppointment.time
                      ? moment(
                        detailAppointment.time,
                        ['HH:mm', 'HH:mm:ss']
                      ).format('HH:mm')
                      : '–'}
                  </strong>
                </div>
              </div>

              {detailImages.length > 0 && (
                <section className="pipeline-detail-modal-section">
                  <h4>Bilder</h4>

                  <div className="pipeline-detail-modal-images">
                    {detailImages.map((url, index) => (
                      <button
                        key={`${url}-${index}`}
                        type="button"
                        onClick={() => {
                          setActiveImageIndex(index);
                          setImageViewerOpen(true);
                        }}
                      >
                        <img
                          src={url}
                          alt={`Beratungsbild ${index + 1}`}
                        />
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {detailAppointment.description && (
                <section className="pipeline-detail-modal-section">
                  <h4>Beschreibung</h4>
                  <p>{detailAppointment.description}</p>
                </section>
              )}

              {detailAppointment.note && (
                <section className="pipeline-detail-modal-section">
                  <h4>Notiz</h4>
                  <p>{detailAppointment.note}</p>
                </section>
              )}

              {(detailAppointment.phone ||
                detailAppointment.email) && (
                  <section className="pipeline-detail-modal-section">
                    <h4>Kontakt</h4>

                    <div className="pipeline-detail-modal-contact">
                      {detailAppointment.phone && (
                        <>
                          <a href={`tel:${detailAppointment.phone}`}>
                            <FiPhone />
                            {detailAppointment.phone}
                          </a>

                          <a
                            href={`https://wa.me/${detailAppointment.phone.replace(
                              /\D/g,
                              ''
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <FaWhatsapp />
                            WhatsApp
                          </a>
                        </>
                      )}

                      {detailAppointment.email && (
                        <a href={`mailto:${detailAppointment.email}`}>
                          <FiMail />
                          {detailAppointment.email}
                        </a>
                      )}
                    </div>
                  </section>
                )}

              <div className="pipeline-detail-modal-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setDetailModalOpen(false);
                    openAppointmentScheduleModal(
                      detailAppointment,
                      {
                        dayId: detailAppointment.dayId,
                        day: detailAppointment.day,
                      }
                    );
                  }}
                >
                  <FiEdit2 />
                  Termin bearbeiten
                </button>

                <button
                  type="button"
                  className="convert"
                  onClick={() => {
                    setAppointmentToConvert(
                      detailAppointment
                    );

                    setDetailModalOpen(false);
                    setConvertModalOpen(true);
                  }}
                >
                  <FiCalendar />
                  In Stechtermin umwandeln
                </button>

                <button
                  type="button"
                  className="danger"
                  onClick={async () => {
                    await deleteConsultationAppointment(
                      detailAppointment.id
                    );

                    setDetailModalOpen(false);
                    setDetailAppointment(null);
                  }}
                >
                  <FiTrash2 />
                  Termin löschen
                </button>
              </div>
            </div>
          </div>
        )}

        {replyItem && <ConsultationReplyDialog key={replyItem.id} item={replyItem} onClose={() => setReplyItem(null)} />}

        <ScheduleConsultationModal
          open={scheduleModalOpen}
          activeItem={scheduleModalItem || activeItem}
          scheduleForm={scheduleForm}
          setScheduleForm={setScheduleForm}
          upcomingConsultationDays={upcomingConsultationDays}
          editingAppointmentId={scheduleAppointmentId}
        invitation={scheduleAppointment?.invitation_id ? {
          id: scheduleAppointment.invitation_id, state: scheduleAppointment.invitation_state,
          expiresMs: scheduleAppointment.invitation_expires_ms,
        } : null}
          canConvert={Boolean(scheduleAppointment) && (!scheduleAppointment.invitation_state || scheduleAppointment.invitation_state === 'confirmed')}
          onConvert={convertScheduledConsultation}
          onClose={() => {
            setScheduleModalOpen(false);
            setScheduleModalItem(null);
            setScheduleAppointmentId(null);
          }}
          onSave={saveConsultationAppointment}
        />

        {manualModalOpen && (
          <div
            className="pipeline-appointment-modal-overlay"
            onClick={() => setManualModalOpen(false)}            >
            <div
              className="pipeline-appointment-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="pipeline-appointment-modal-close"
                onClick={() => setManualModalOpen(false)}
              >
                ×
              </button>

              <h3>Anfrage manuell anlegen</h3>

              <div className="pipeline-appointment-edit-grid">
                <label>
                  Name
                  <input
                    value={manualForm.clientName}
                    onChange={(e) =>
                      setManualForm(prev => ({ ...prev, clientName: e.target.value }))
                    }
                    placeholder="Kundenname"
                  />
                </label>

                <label>
                  Quelle
                  <select
                    value={manualForm.source}
                    onChange={(e) =>
                      setManualForm(prev => ({ ...prev, source: e.target.value }))
                    }
                  >
                    <option value="phone">Telefon</option>
                    <option value="manual">Manuell</option>
                    <option value="instagram">Instagram</option>
                    <option value="other">Sonstige</option>
                  </select>
                </label>
              </div>

              <div className="pipeline-appointment-edit-grid">
                <label>
                  Telefon
                  <input
                    value={manualForm.phone}
                    onChange={(e) =>
                      setManualForm(prev => ({ ...prev, phone: e.target.value }))
                    }
                    placeholder="Telefonnummer"
                  />
                </label>

                <label>
                  E-Mail
                  <input
                    value={manualForm.email}
                    onChange={(e) =>
                      setManualForm(prev => ({ ...prev, email: e.target.value }))
                    }
                    placeholder="E-Mail"
                  />
                </label>
              </div>

              <label className="pipeline-modal-full-label">
                Betreff / Wunsch
                <input
                  value={manualForm.subject}
                  onChange={(e) =>
                    setManualForm(prev => ({ ...prev, subject: e.target.value }))
                  }
                  placeholder="z.B. Fineline Sleeve"
                />
              </label>

              <label className="pipeline-modal-full-label">
                Beschreibung
                <textarea
                  value={manualForm.description}
                  onChange={(e) =>
                    setManualForm(prev => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="Was möchte die Person?"
                />
              </label>

              {!editingConsultationId&&<DirectBookingFields form={manualForm} setForm={setManualForm} days={upcomingConsultationDays} disabled={manualSaving}/>}
              {manualError&&<p role="alert" className="consultation-email-error">{manualError}</p>}
              <div className="pipeline-appointment-modal-actions">
                <button disabled={manualSaving} onClick={() => setManualModalOpen(false)}>
                  Abbrechen
                </button>

                <button className="primary" disabled={manualSaving} onClick={saveManualConsultation}>
                  {manualSaving ? "Wird gespeichert …" : manualForm.bookNow && !editingConsultationId ? "Termin direkt buchen" : "Anfrage speichern"}
                </button>
              </div>
            </div>
          </div>
        )}

        {consultationDayModalOpen && (
          <div
            className="pipeline-appointment-modal-overlay"
            onClick={() => setConsultationDayModalOpen(false)}
          >
            <div
              className="pipeline-appointment-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="pipeline-appointment-modal-close"
                onClick={() => setConsultationDayModalOpen(false)}
              >
                ×
              </button>

              <h3>Beratungstag anlegen</h3>

              <label className="pipeline-modal-full-label">
                Datum
                <input
                  type="date"
                  value={newConsultationDay}
                  onChange={(e) => setNewConsultationDay(e.target.value)}
                />
              </label>

              <div className="pipeline-appointment-modal-actions">
                <button onClick={() => setConsultationDayModalOpen(false)}>
                  Abbrechen
                </button>

                <button className="primary" onClick={saveConsultationDay}>
                  Beratungstag anlegen
                </button>
              </div>

            </div>
          </div>
        )}

        {convertModalOpen && appointmentToConvert && (
          <ConvertToTattooModal
            appointment={appointmentToConvert}
            selectedLocationId={selectedLocationId}
            locations={locations}
            onClose={() => {
              setConvertModalOpen(false);
              setAppointmentToConvert(null);
            }}
            onSuccess={(data) => {
              setConvertModalOpen(false);
              setAppointmentToConvert(null);

              navigate('/', {
                state: {
                  targetLocationId: data.targetLocationId,
                  highlightAppointment: {
                    appointmentId: data.appointmentId,
                    appointmentIds: data.appointmentIds || [],
                    dayId: data.availableDayId,
                  },
                },
              });
            }}
          />
        )}


      </section>
    </div>

  );


};

export default ConsultationView;
// prime-ink-cancellation-alerts-v1
